<template>
  <div :class="isDialog ? '' : 'px-5 py-4 flex justify-center'">
    <div class="sell-membership-card flex flex-col" :class="{ 'sell-membership-card--dialog': isDialog }"
      style="gap: 1.5rem;">
      <v-btn icon class="sell-membership-close" @click="cancel()">
        <v-icon color="plusGreyDarken4">mdi-close</v-icon>
      </v-btn>
      <div>
        <div class="font-roboto plusGreyDarken4--text text-h5">
          {{ $t("message.products_memberships_plus.sell_membership") }}
        </div>
        <div class="plusGreyDarken2--text text-body-1 mt-2">
          {{ $t("message.products_memberships_plus.select_membership_assign_member") }}
        </div>
      </div>

      <v-progress-linear v-if="loading && !product" color="plusIndigoDarken4" indeterminate height="4" class="mb-2" />

      <div class="sell-product-fields flex flex-col" style="gap: 2rem;">
        <template v-if="showSubscriptionPackagePickers">
          <v-autocomplete v-model="selectedSubscription" :items="subscriptions" :label="$t('message.pick_subscription')"
            item-text="text" item-value="value" clearable outlined dense hide-details>
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-if="item.header" v-bind="attrs" :key="item.header" disabled>
                <v-list-item-content>
                  <v-list-item-title class="text-caption font-weight-medium">{{ item.header }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-else v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <span>{{ currencySymbol }}{{ item.amountDecimal }}</span>
                    —
                    {{ item.automatic ? $t('message.interval_' + item.frequency) : $t('message.manual') }}
                    <span v-if="item.customDaysInterval">({{ item.customDaysInterval }} {{ $t('message.days')
                      }})</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-autocomplete v-if="availablePackages && availablePackages.length" hide-details
            @change="selectProductsPackage" v-model="selectedPackage" :items="availablePackages"
            :label="$t('message.pick_package')" item-text="name" item-value="_id" clearable outlined dense>
            <template v-slot:item="data">
              <v-list-item-content v-if="data.item">
                <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ data.item.description }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </template>

        <template v-else-if="product">
          <v-select readonly outlined dense hide-details :value="product.name" :items="[product.name]"
            :label="$t('message.product')"></v-select>
        </template>

        <v-autocomplete v-if="!assignToVisitor && (product || showSubscriptionPackagePickers)" hide-details
          v-model="selectedMember" :items="members" :label="$t('message.sell_product_plus_pick_member')" item-text="text" item-value="value" clearable
          outlined dense></v-autocomplete>

        <v-text-field v-if="assignToVisitor" outlined dense hide-details :label="$t('message.email')"
          v-model="email"></v-text-field>

        <PaymentTypeSelect v-if="product || showSubscriptionPackagePickers" v-model="paidWith" />

        <template v-if="product">
          <template v-if="product.type == 'service'">
            <div class="flex flex-col" style="gap: 1.25rem;">
              <v-switch color="plusIndigoDarken4" inset hide-details v-model="startInFuture"
                :label="$t('message.start_in_future')"></v-switch>

              <template v-if="startInFuture">
                <div v-if="isCustomAchPaymentOption()" class="sell-warning-banner"
                  :style="isDarkMode() ? { background: 'rgba(255, 204, 128, 0.1)', border: '1px solid #FFCC80' } : {}">
                  <v-icon size="20" color="plusOrangeDarken2">mdi-alert-circle-outline</v-icon>
                  <span class="text-body-2">
                    When setting up a debit payment for a membership that starts in the future, <b>a one-time 1 {{
                      currency }}</b> charge will occur at the time you or the user enter the bank information.
                    This is required to comply with mandates due to banking regulations in your country.
                  </span>
                </div>

                <div class="sell-warning-banner"
                  :style="isDarkMode() ? { background: 'rgba(255, 204, 128, 0.1)', border: '1px solid #FFCC80' } : {}">
                  <v-icon size="20" color="plusOrangeDarken2">mdi-alert-circle-outline</v-icon>
                  <span class="text-body-2">{{ $t("message.trial_explained") }}</span>
                </div>

                <div class="sell-date-field">
                  <span class="sell-date-field-label plusGreyDarken2--text">{{ $t('message.subscription_start_date')
                    }}</span>
                  <DatePicker :show-time="false" v-model="trialPeriodDate" :past-event="false"></DatePicker>
                </div>
              </template>
            </div>

            <div v-if="academyBillDay && isMonthlyMembership()" class="flex flex-col" style="gap: 1.25rem;">
              <v-switch color="plusIndigoDarken4" inset hide-details v-model="startInAcademyDate"
                :label="$t('message.use_academy_bill_day')"></v-switch>

              <template v-if="startInAcademyDate">
                <v-text-field outlined dense disabled hide-details :label="$t('message.next_academy_bill_day')"
                  v-model="futureBillDateString"></v-text-field>

                <div class="sell-warning-banner"
                  :style="isDarkMode() ? { background: 'rgba(255, 204, 128, 0.1)', border: '1px solid #FFCC80' } : {}">
                  <v-icon size="20" color="plusOrangeDarken2">mdi-alert-circle-outline</v-icon>
                  <span class="text-body-2">
                    {{ $t("message.prorated_bill_explained") }}: <b>{{ proratedAmount }} {{ currency }}</b>{{ ' ' }}
                    <span class="text-uppercase">({{ proratedDays }} {{ $t("message.days") }})</span>
                  </span>
                </div>
              </template>
            </div>

            <v-switch
              v-if="startInFuture && !isCustomAchPaymentOption() && paymentsModule != 'easypay' && !(academyBillDay && isMonthlyMembership())"
              color="plusIndigoDarken4" inset hide-details v-model="addProratedAmount"
              :label="$t('message.add_prorated_amount')"></v-switch>
          </template>

          <div v-if="product.type == 'good' && product.isMembership && selectedMember && !checkoutInitialized" class="flex flex-col" style="gap: 1.25rem;">
            <v-switch color="plusIndigoDarken4" inset hide-details disabled v-model="isSubscription"
              :label="$t('message.count_as_subscription')"></v-switch>
            <v-switch color="plusIndigoDarken4" inset hide-details v-model="product.manualRecurrence"
              :label="$t('message.keep_membership_visible')"></v-switch>
            <template v-if="isSubscription">
              <div>
                <v-switch color="plusIndigoDarken4" inset hide-details v-model="subscriptionStartDateCheckbox"
                  :label="$t('message.different_transaction_date')"></v-switch>
                <div v-if="subscriptionStartDateCheckbox" class="mt-2">
                  <DatePicker :show-time="false" v-model="subscriptionStartDate" :no-boundries="true"></DatePicker>
                </div>
              </div>
              <div>
                <v-switch color="plusIndigoDarken4" inset hide-details v-model="subscriptionEndDateCheckbox"
                  :label="$t('message.end_on_date')"></v-switch>
                <div v-if="subscriptionEndDateCheckbox" class="mt-2">
                  <DatePicker :show-time="false" v-model="subscriptionEndDate" :no-boundries="true"></DatePicker>
                </div>
              </div>
              <div>
                <v-switch color="plusIndigoDarken4" inset hide-details v-model="subscriptionEndCheckinsCheckbox"
                  :label="$t('message.end_on_checkins')"></v-switch>
                <v-text-field v-if="subscriptionEndCheckinsCheckbox" outlined dense hide-details class="mt-2"
                  v-model="subscriptionCheckins" type="number"
                  :label="$t('message.subscription_checkins')" :rules="[validationRules.number]"></v-text-field>
              </div>
            </template>
          </div>

          <div v-if="(product.isMembership || product.type == 'service') && contracts.length > 0" class="flex flex-col" style="gap: 1.25rem;">
            <v-switch color="plusIndigoDarken4" inset hide-details v-model="assignContract"
              :label="$t('message.assign_contract')"></v-switch>
            <v-autocomplete v-if="assignContract" v-model="selectedContract" :label="$t('message.contract')"
              :items="contracts" item-text="text" item-value="value" clearable outlined dense hide-details></v-autocomplete>
          </div>

          <v-switch color="plusIndigoDarken4" inset hide-details v-model="applyDiscount"
            :label="$t('message.sell_product_plus_apply_discount')"></v-switch>

          <v-text-field outlined dense hide-details class="pt-2"
            v-show="applyDiscount && (!product.isMembership || extraProducts.length == 0)" v-model="discountPercent"
            :label="$t('message.sell_product_plus_discount_percentage')" :rules="[validationRules.number]"></v-text-field>

          <v-text-field hide-details :disabled="discountPercent > 0" dense outlined v-show="applyDiscount"
            v-model="discountAmount" :label="$t('message.sell_product_plus_discount_value')" :rules="[validationRules.decimal]"></v-text-field>

          <div v-if="applyDiscount && product.type == 'service'">
            <v-switch color="plusIndigoDarken4" inset hide-details v-model="discountExpires"
              :label="$t('message.expires')"></v-switch>
          </div>
          <v-text-field v-if="discountExpires" class="mt-2" hide-details type="number"
            v-model="discountDurationInMonths" dense outlined :label="$t('message.sell_product_plus_duration_in_months')"></v-text-field>

          <v-switch v-if="showAddMoreProductsSwitch" color="plusIndigoDarken4" inset hide-details
            v-model="addExtraProductSwitch" :label="$t('message.sell_product_plus_add_more_products')"></v-switch>
          <div v-if="showAddMoreProductsSwitch && addExtraProductSwitch">
            <v-autocomplete :return-object="true" @change="addExtraProduct()" v-model="selectedExtraProduct"
              :items="availableProducts" :label="$t('message.sell_product_plus_select_a_product')" item-text="text"
              clearable outlined dense hide-details class="mt-2"></v-autocomplete>
            <v-list v-if="extraProducts.length > 0" class="sell-extra-list pt-8">

              <v-list-item v-for="(i, index) in extraProducts" :key="'extra_product_item_' + index"
                class="py-4 mb-2 plusGreyLighten5">
                <v-list-item-avatar>
                  <v-avatar size="42" color="grey lighten-4">
                    <ImageDisplay v-if="i.image" :src="i.image" />
                    <img v-else :src="assets('logo-circular-watermarked.png')" alt="" />
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-1 font-weight-medium plusGreyDarken3--text">{{ i.name
                  }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 plusGreyDarken2--text">{{ i.description || '—'
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-checkbox dense hide-details class="mt-0 pt-0" color="plusIndigoDarken4" :input-value="!!i.free"
                    @change="toggleExtraProductFree(i)">
                    <template v-slot:label>
                      <span class="text-body-2 plusGreyDarken3--text">{{ $t('message.sell_product_plus_mark_as_free_item') }}</span>
                    </template>
                  </v-checkbox>
                </v-list-item-action>
                <v-list-item-action>
                  <v-btn small icon @click="removeExtraProduct(i)">
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <v-text-field v-if="startInAcademyDate" outlined dense disabled hide-details
            :label="$t('message.subscription_start_date')" v-model="futureBillDateString"></v-text-field>

          <div v-if="displaySaleTotal != null">
            <v-divider class="mb-5"></v-divider>
            <div class="font-weight-bold plusGreyDarken4--text text-body-1">
              {{ $t('message.total') }}: {{ currencySymbol }}{{ displaySaleTotal }} {{ currency }}
            </div>
          </div>
        </template>
      </div>

      <div class="flex flex-wrap" style="gap: 1.25rem;">
        <PlusButton @click="cancel()">{{ $t("message.cancel") }}</PlusButton>

        <PlusButton v-if="!paymentsReady && product && product.type == 'good'" :disabled="handlingCharges || !validQuantity ||
          (!email && !selectedMember)
          " intent="primary" @click="payCash()">
          <span>{{ $t('message.products_memberships_plus.sell_membership') }}</span>
        </PlusButton>

        <PlusButton :disabled="!stripeReady || handlingCharges || productId == 'manual-membership'" v-if="paymentsReady &&
          product &&
          product.amount > 0 &&
          !subscriptionStartDateCheckbox &&
          discountedTotal != 0 &&
          validQuantity &&
          (email || selectedMember)
        " intent="primary" @click="initCheckout()">
          <span>{{ $t('message.products_memberships_plus.sell_membership') }}</span>
        </PlusButton>
        <!-- <PlusButton :disabled="!stripeReady || handlingCharges" v-if="paymentsReady &&
          product && product.amount > 0 && validQuantity && selectedMember && !subscriptionStartDateCheckbox &&
          discountedTotal != 0
        " intent="primary" @click="initCheckout(true)">
          <span>{{ $t('message.send_request') }}</span>
        </PlusButton> -->
      </div>
    </div>
  </div>
</template>

<script>
import PlusButton from "@/components/plus/PlusButton";
import PaymentTypeSelect from "@/components/inputs/PaymentTypeSelect.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";
import { Environment } from "@/services/Environment";

export default {
  props: {
    productIdProp: { type: String, default: null },
    packageIdProp: { type: String, default: null },
    isDialog: { type: Boolean, default: false },
  },
  data() {
    return {
      productId: this.productIdProp || this.$route.params.productId,
      packageId: this.packageIdProp || this.$route.query.packageId,
      visitorEmail: this.$route.query.visitorEmail,
      selectedMember: this.$route.query.userId
        ? this.$route.query.userId
        : null,
      loading: true,
      stripeSessionId: null,
      errorMessage: null,
      addExtraProductSwitch: false,
      isSubscription: false,
      showMOreOptions: false,
      subscriptionEndDate: null,
      subscriptionCheckins: null,
      subscriptionEndDateCheckbox: false,
      subscriptionEndCheckinsCheckbox: false,
      subscriptionStartDate: null,
      subscriptionStartDateCheckbox: false,
      subscriptionStartCheckinsCheckbox: false,
      subscriptionStartDatePickerDialog: null,
      checkoutInitialized: false,
      product: null,
      extraOptionsPanel: null,
      startInFuture: false,
      startInAcademyDate: false,
      datePickerDialog: null,
      paymentTypes: [],
      selectedPaymentOptions: [],
      subscriptionEndDatePickerDialog: false,
      validQuantity: true,
      saleForm: true,
      addProratedAmount: false,
      members: [],
      back: false,
      assignToMember: false,
      assignToVisitor: false,
      quantity: 1,
      academyImage: null,
      paymentForm: false,
      paymentMethod: null,
      currency: null,
      paidWith: null,
      currencySymbol: '$',
      hideQuantity: false,
      trialPeriodDays: 0,
      saveCreditCard: true,
      trialPeriodDate: null,
      loadingPackages: false,
      academyBillDay: null,
      futureBillDateString: null,
      proratedAmount: null,
      paymentsModule: null,
      proratedDays: null,
      proratedAmountPerDay: null,
      contracts: [],
      selectedSubscription: null,
      selectedContract: null,
      email: null,
      subscriptions: [],
      availableProducts: [],
      availablePackages: [],
      selectedPackage: null,
      stripeInstance: null,
      clientSecret: null,
      stripeCustomerId: null,
      paymentIntentId: null,
      customAch: false,
      customAchData: null,
      customAchPostData: null,
      mainStripeAccountId: null,
      stripeReady: false,
      handlingCharges: false,
      terminalStripeAccountId: null,
      paymentsReady: false,
      automaticTaxes: false,
      canUseTerminal: true,
      altStripeAccount: null,
      extraProducts: [],
      selectedExtraProduct: null,
      totalExtra: 0,
      applyDiscount: false,
      assignContract: false,
      discountPercent: null,
      discountAmount: null,
      discountedTotal: null,
      terminal: null,//Stripe terminal with the data
      terminalAvailable: false,
      terminalLocation: null,
      readerInUse: null,
      waitingForReaderPayment: false,
      readerDisconnected: false,
      simulatedTerminal: false,
      discountDurationInMonths: null,
      discountExpires: null
    };
  },
  components: {
    PlusButton,
    PaymentTypeSelect,
    DatePicker
  },
  computed: {
    showSubscriptionPackagePickers() {
      return (this.assignToMember || this.assignToVisitor) && this.$route.params.productId !== "package";
    },
    showAddMoreProductsSwitch() {
      return (
        this.product &&
        (this.product.isMembership || this.product.type === "service") &&
        !this.assignToVisitor
      );
    },
    displaySaleTotal() {
      if (!this.product) return null;
      const base =
        (Number(this.product.amountDecimal) || 0) * (Number(this.quantity) || 1) +
        (Number(this.totalExtra) || 0);
      if (this.applyDiscount && this.discountedTotal != null && !isNaN(this.discountedTotal)) {
        return this.discountedTotal;
      }
      return base;
    }
  },
  created() {
    const now = new Date();
    now.setDate(now.getDate() + 3);
    this.simulatedTerminal = Environment.get("VUE_APP_SIMULATED_TERMINAL") == "true" ? true : false;
    console.log('Started page with simulated terminal mode: ', this.simulatedTerminal)
    Api()
      .get(
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/is_connected/all"
      )
      .then((response) => {
        this.stripeReady = response.data.stripeConnected;
        if (!this.stripeReady && response.data.paymentsModule == 'easypay' && response.data.paymentsReady) {
          this.stripeReady = true;
        }
        if (this.selectedMember) {
          this.getPaymentMethod();
        }
      });
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        this.currency = response.data.currency ? response.data.currency.toUpperCase() : 'USD';
        this.currencySymbol = response.data.currencySymbol ? response.data.currencySymbol : '$';
        this.paymentsModule = response.data.paymentsModule;
        this.academyImage = response.data.image;
        this.mainStripeAccountId = response.data.stripeAccountId;
        this.customAch = response.data.customAch;
        this.academyBillDay = response.data.academyBillDay;
        this.paymentsReady =
          response.data.paymentsReady && response.data.stripeAccountId;
        if (!this.paymentsReady && response.data.paymentsModule == 'easypay')
          this.paymentsReady = true;
        if (!this.paymentsReady && response.data.paymentsModule == 'infinicept')
          this.paymentsReady = true;
        if (!this.paymentsReady)
          this.paidWith = 'cash';
        this.automaticTaxes = response.data.configuration ? response.data.configuration.automaticTaxCalculation : false;
        if (this.productId == "membership" || this.productId == "manual-membership") {
          this.getPackages();
          this.validQuantity = true;
          if (this.productId == "membership")
            this.assignToMember = true;
          else if (this.productId == "manual-membership")
            this.assignToVisitor = true;
          Api()
            .get(
              "admins/academy/" + this.$store.state.managedAcademy + "/products "
            )
            .then((response) => {
              const data = response.data;
              if (data) {
                this.loading = false;
                if (this.productId == "membership") {
                  if (this.paymentsReady && data.subscriptions && data.subscriptions.length > 0) {
                    this.subscriptions.push({ header: 'Automatic' });
                    for (const p of data.subscriptions) {
                      this.subscriptions.push({ value: p._id, text: p.name, frequency: p.interval, automatic: true, amountDecimal: p.amountDecimal, customDaysInterval: p.customDaysInterval });
                    }
                  }
                }
                if (this.visitorEmail)
                  this.email = this.visitorEmail;
                if (data.goods && data.goods.length > 0) {
                  this.subscriptions.push({ header: 'Manual' });
                  for (const p of data.goods) {
                    if (p.isMembership && p.active) {
                      this.subscriptions.push({ value: p._id, text: p.name, frequency: 'manual', automatic: false, amountDecimal: p.amountDecimal });
                    }
                  }
                }
              }
            })
            .catch((e) => {
              if (e)
                this.showAlert("error", this.$t("message.error_generic"));
            });
        } else if (this.productId == "package") {
          this.loadProductsPackage();
        } else {
          this.loadProduct();
        }
      });
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
      .then((response) => {
        const data = response.data;
        if (data) {
          for (const m of data.approved) {
            this.members.push({ value: m._id, text: m.fName + " " + m.lName });
          }
        }
      })
      .catch((e) => {
        if (e)
          this.showAlert("error", this.$t("message.error_generic"));
      });
  },
  mounted() {
    let stripeTerminalScript = document.createElement('script')
    stripeTerminalScript.setAttribute('src', 'https://js.stripe.com/terminal/v1/')
    document.head.appendChild(stripeTerminalScript);
  },
  watch: {//getDiscountedPrice
    discountPercent: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.product && this.quantity) {
          let originalAmount = this.product.amountDecimal * this.quantity + this.totalExtra;
          this.discountedTotal = this.getDiscountedPrice(originalAmount, newValue);
        }
      },
    },
    discountAmount: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.product && this.quantity) {
          let originalAmount = this.product.amountDecimal * this.quantity + this.totalExtra;
          this.discountedTotal = this.getDiscountedPrice(originalAmount, newValue, newValue);
        }
      },
    },
    quantity: {
      handler: function (newValue) {
        this.checkAmountValidity(newValue);
      },
    },
    selectedSubscription: {
      handler: function (newValue) {
        this.errorMessage = null;
        if (newValue) {
          this.productId = newValue;
          this.loadProduct();
        } else {
          this.productId = this.$route.params.productId;
          this.product = null;
        }
      },
    },
    selectedMember: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          if (this.paymentsReady)
            this.getPaymentMethod();
          this.validateMember()
        }
      },
    },
    startInAcademyDate: {
      handler: function (newValue) {
        if (newValue) {
          this.trialPeriodDate = null;
          this.startInFuture = false;
          this.processProratedAmount();
        }
      },
    },
    trialPeriodDate: {
      handler: function (newValue) {
        if (newValue && this.addProratedAmount) {
          this.processProratedAmount();
        }
      },
    },
    addProratedAmount: {
      handler: function (newValue) {
        if (newValue && this.trialPeriodDate) {
          this.processProratedAmount();
        }
      },
    },
    startInFuture: {
      handler: function (newValue) {
        if (newValue) {
          this.startInAcademyDate = false;
          if (!this.trialPeriodDate) {
            const defaultStart = new Date();
            defaultStart.setDate(defaultStart.getDate() + 3);
            this.trialPeriodDate = defaultStart;
          }
        } else if (!this.startInAcademyDate) {
          this.trialPeriodDate = null;
        }
      },
    },
    addExtraProductSwitch: {
      handler: function (enabled) {
        if (enabled && this.product) {
          this.getExtraProducts();
        }
      },
    },
  },
  methods: {
    isAtLeastThreeDays(futureDate) {
      const today = new Date();
      const targetDate = new Date(futureDate);
      today.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      const diffTime = targetDate.getTime() - today.getTime();
      const ONE_DAY_MS = 86400000;
      const diffDays = Math.floor(diffTime / ONE_DAY_MS);
      return diffDays >= 3;
    },
    selectProductsPackage(e) {
      this.packageId = e;
      this.loadProductsPackage();
    },
    getTransactionTotal() {
      let total = 0;
      if (this.applyDiscount && this.discountedTotal) {
        total = this.discountedTotal;
      } else {
        total = this.totalExtra + this.product.amountDecimal;
      }
      return total;
    },
    removeExtraProduct(i) {
      let index = 0;
      for (const p of this.extraProducts) {
        if (p._id == i._id) {
          this.extraProducts.splice(index, 1);
          break;
        }
        index++;
      }
      this.calculateTotalExtra();
    },
    toggleExtraProductFree(i) {
      if (i.free)
        i.free = false;
      else
        i.free = true;
      this.calculateTotalExtra();
    },
    calculateTotalExtra() {
      let total = 0;
      for (const p of this.extraProducts) {
        if (!p.free) {
          total += p.amountDecimal;
        } else {
          total += 0;
        }
      }

      this.totalExtra = total;
      if (this.discountAmount > 0 || this.discountPercent > 0) {
        this.discountedTotal = this.getDiscountedPrice(this.product.amountDecimal + total, this.discountPercent, this.discountAmount);
      }
    },
    addExtraProduct() {
      const p = this.selectedExtraProduct;
      if (p && p.product) {
        const row = Object.assign({}, p.product, { free: false });
        this.extraProducts.push(row);
        this.calculateTotalExtra();
        this.$nextTick(() => {
          this.selectedExtraProduct = null;
        });
      }
    },
    getExtraProducts() {
      Api()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/products?category=retail&check_availability=true"
        )
        .then((response) => {
          const data = response.data;
          if (data && this.product) {
            this.availableProducts = [];
            for (const i of data.goods) {
              let taxMatches = true;
              if (this.product.taxBehavior && i.taxBehavior != this.product.taxBehavior)
                taxMatches = false;
              if (taxMatches) {
                this.availableProducts.push({ text: i.name + ' (' + (this.currency + i.amountDecimal) + ')', value: i._id, product: i })
              }
            }
          }
        })
        .catch((e) => {
          console.log('859: ', e)
          this.loading = false;
          if (e)
            this.showAlert(
              "error",
              this.$t("message.sell_product_plus_oops"),
              this.$t("message.error_generic")
            );
        });
    },
    cancelTerminalTransaction() {
      if (this.readerInUse) {
        const vm = this;
        try {
          this.terminal.cancelCollectSetupIntentPaymentMethod().then(function (result) {
            if (result.error) {
              vm.waitingForReaderPayment = false;
              vm.readerInUse = null;
              vm.handlingCharges = false;
            } else {
              // Placeholder for processing result.paymentIntent
              vm.waitingForReaderPayment = false;
              vm.readerInUse = null;
              vm.handlingCharges = false;
            }
          });
        } catch (e) {
          console.log('886: ', e);
          vm.waitingForReaderPayment = false;
          vm.readerInUse = null;
          vm.handlingCharges = false;
        }
      }
    },
    getDiscountExpirationDate() {
      let d = null;
      if (this.discountDurationInMonths) {
        const expDate = new Date();
        expDate.setMonth(expDate.getMonth() + new Number(this.discountDurationInMonths).valueOf())
        d = expDate;
      }
      return d;
    },
    initTerminalCheckout() {
      this.waitingForReaderPayment = false;
      if (this.handlingCharges)
        return;
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      this.back = false;
      this.handlingCharges = true;
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }

      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      let prorated = null;
      if (this.product.type == "service" && this.startInAcademyDate && this.proratedAmount > 0) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
        const today = new Date();
        const academyBillDate = new Date();
        academyBillDate.setDate(this.academyBillDay);
        academyBillDate.setHours(1);
        const daysBetween = this.daysBetween(academyBillDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          academyBillDate.setMonth(academyBillDate.getMonth() + 1);
        }
        this.trialPeriodDate = new Date(academyBillDate);
      } else if (this.product.type == "service" && this.startInFuture && this.addProratedAmount) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
      }
      if (this.trialPeriodDate) {
        this.trialPeriodDate.setHours(4);
        if (!this.isAtLeastThreeDays(this.trialPeriodDate)) {
          this.showAlert("error", this.$t("message.trial_end_too_close"));
          this.handlingCharges = false;
          return;
        }
      }
      const discountExpirationDate = this.getDiscountExpirationDate();
      let postData = {
        product: this.productId,
        processedBy: "terminal",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        discountedTotal: this.discountedTotal,
        discountDuration: this.discountDurationInMonths ? new Number(this.discountDurationInMonths).valueOf() : null,
        discountExpirationDate: discountExpirationDate,
        quantity: 1,
        prorated: prorated,
        buyer: this.selectedMember,
        email: this.email,
        documentTemplate: this.selectedContract,
        manualRecurrence: this.product.manualRecurrence,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        trialPeriodEndDate: this.startInFuture || this.startInAcademyDate ? this.trialPeriodDate : null,
      };
      let postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/terminals/init/payment";
      if (this.paymentsModule == 'infinicept') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.getTransactionTotal();
        this.navigateTo({
          name: "inifnicept-checkout",
          params: { transactionData: postData }
        });
        return;
      } else if (this.paymentsModule == 'easypay') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.discountedTotal ? this.discountedTotal : this.transactionTotal;
        postData.paymentsModule = 'easypay';
        postData.routeFrom = 'regularsell';
        postData.productData = this.product;
        this.navigateTo({
          name: "easypay-checkout",
          params: { transactionData: postData }
        });
        return;
      }
      Api()
        .post(postUrl, postData)
        .then((response) => {
          let setupMode = true;
          const clientSecret = response.data.clientSecret;
          postData.customer = response.data.customer;
          if (response.data.setupIntent) {
            postData.setupIntent = response.data.setupIntent;
            setupMode = true;
          } else if (response.data.paymentIntent) {
            postData.paymentIntent = response.data.paymentIntent;
            setupMode = false;
          }

          this.waitingForReaderPayment = true;
          //Now we call the terminal and ask for a setupIntent
          if (setupMode)
            return this.terminalPaymentSetupMode(clientSecret, postData);
          else
            return this.terminalPaymentRegularMode(clientSecret, postData);
        })
        .catch((e) => {
          this.waitingForReaderPayment = false;
          this.handlingCharges = false;
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    terminalPaymentSetupMode(clientSecret, postData) {
      const vm = this;
      this.terminal.collectSetupIntentPaymentMethod(clientSecret, true).then(function (collectResult) {
        if (collectResult.error) {
          if (collectResult.error.code != 'canceled') {
            vm.showAlert("error", vm.$t("message.error_generic"));
            vm.waitingForReaderPayment = false;
            vm.handlingCharges = false;
          }
        } else {
          // Placeholder for confirming result.setupIntent
          vm.terminal.confirmSetupIntent(collectResult.setupIntent).then(function (processSetupResult) {
            if (processSetupResult.error) {
              vm.showAlert("error", vm.$t("message.error_generic"));
              vm.waitingForReaderPayment = false;
              vm.handlingCharges = false;
            } else if (processSetupResult.setupIntent) {
              vm.completeTerminalPurchase(postData);
            }
          });
        }
      });
    },
    terminalPaymentRegularMode(clientSecret, postData) {
      const vm = this;
      this.terminal.collectPaymentMethod(clientSecret).then(function (collectResult) {
        if (collectResult.error) {
          vm.handlingCharges = false;
          vm.waitingForReaderPayment = false;
          if (collectResult.error.code != 'canceled')
            vm.showAlert("error", vm.$t("message.error_generic"));
        } else {
          // Placeholder for processing result.paymentIntent
          vm.terminal.processPayment(collectResult.paymentIntent).then(function (processPaymentResult) {
            if (processPaymentResult.error) {
              vm.showAlert("error", vm.$t("message.error_generic"));
            } else if (processPaymentResult.paymentIntent) {
              vm.completeTerminalPurchase(postData);
              vm.waitingForReaderPayment = false;
              vm.clearCart();
              vm.handlingCharges = false;
            }
          });
        }
      });
    },
    completeTerminalPurchase(postData) {
      //Send it to the back end to record our transaction
      Api()
        .post(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/terminals/complete/subscription", postData
        )
        .then((response) => {
          if (response.data) {
            this.waitingForReaderPayment = false;
            this.handlingCharges = false;
            this.showAlert(
              "success",
              this.$t("message.payment_completed")
            );
            this.navigateTo({ name: 'viewtransaction', params: { sessionId: response.data.transaction._id } });
          }
        }).catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.waitingForReaderPayment = false;
          this.handlingCharges = false;
        });
    },
    getTerminalAvailability() {
      Api()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/terminals/"
        )
        .then((response) => {
          if (response.data && response.data.locations && response.data.locations.length > 0) {
            //If we have a connected reader, connect, if not, move on
            this.terminalLocation = response.data.locations[0].id;
            this.terminalStripeAccountId = response.data.locations[0].stripeAccount;
            if (this.terminalStripeAccountId != this.mainStripeAccountId) {
              this.canUseTerminal = false;
            }
            if (this.product) {
              if (this.product.altStripeAccount && this.product.altStripeAccount == this.terminalStripeAccountId)
                this.connectToReader();
              else if (!this.product.altStripeAccount)
                this.connectToReader();
            }
          } else if (this.simulatedTerminal) {
            console.log('No terminals available but we are on simulated terminal mode: ', this.simulatedTerminal)
            this.connectToReader();
          }
        });
    },
    refreshReaderConnection() {
      if (this.terminalLocation || this.simulatedTerminal) {
        this.connectToReader();
      }
    },
    connectToReader() {
      this.readerDisconnected = false;
      this.readerInUse = null;
      const vm = this;
      const loc = this.terminalLocation;
      this.connectingReader = true;
      let config = null;
      if (this.simulatedTerminal) {
        console.log('Skipping terminal connection , simulated reader terminal');
        /*eslint-disable no-undef*/
        this.terminal = StripeTerminal.create({
          onFetchConnectionToken: function () {
            return Api()
              .post("payments/academy/" + vm.$store.state.managedAcademy + "/terminals/connection_token", { simulated: true })
              .then((response) => {
                return response.data.secret;
              })
          },
          onUnexpectedReaderDisconnect: function () {
            vm.readerDisconnected = true;
            vm.showAlert("error", this.$t("message.reader_disconnected"));
          },
        });
        config = { simulated: true };
      } else {
        /*eslint-disable no-undef*/
        this.terminal = StripeTerminal.create({
          onFetchConnectionToken: function () {
            return Api()
              .post("payments/academy/" + vm.$store.state.managedAcademy + "/terminals/connection_token", { location: loc, stripeAccountId: this.terminalStripeAccountId })
              .then((response) => {
                return response.data.secret;
              })
          },
          onUnexpectedReaderDisconnect: function () {
            vm.readerDisconnected = true;
            vm.showAlert("error", this.$t("message.reader_disconnected"));
          },
        });
        config = { simulated: false, location: loc };
      }

      this.terminal.discoverReaders(config).then(function (discoverResult) {
        if (discoverResult.error) {
          vm.showAlert("error", this.$t("message.error_generic"));
          vm.connectingReader = false;
        } else if (discoverResult.discoveredReaders.length === 0) {
          vm.showAlert("error", this.$t("message.no_readers_available"));
          vm.connectingReader = false;
        } else {
          //We are only grabbing 1 reader, the id matches the one we should have gotten from the beginning
          const discoveredReader = discoverResult.discoveredReaders[0];
          //We got a reader, lets connect
          vm.terminal.connectReader(discoveredReader).then(function (connectResult) {
            if (connectResult.error) {
              vm.showAlert("error", vm.$t("message.connection_error"));
              vm.connectingReader = false;
              vm.terminalAvailable = false;
            } else {
              vm.terminalAvailable = true;
              vm.connectingReader = false;
              vm.canUseTerminal = true;
              vm.readerInUse = discoveredReader.id;
            }
          });
        }
      });
    },
    getPackages() {
      this.loadingPackages = true;
      Api()
        .get("admins/managedacademy/" + this.$store.state.managedAcademy + "/products/packages")
        .then((response) => {
          this.availablePackages = response.data.data;
          this.loadingPackages = false;
        })
        .catch((e) => {
          if (e) {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    getPaymentTypes() {
      Api()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/paymenttypes"
        )
        .then((response) => {
          if (response.data && response.data.paymentMethods && response.data.paymentMethods.length == 2) {
            this.paymentTypes.push({ text: this.$t("message.credit_card"), value: 'card' });
            this.paymentTypes.push({ text: this.$t("message.ach"), value: response.data.paymentMethods[1] });
            this.selectedPaymentOptions.push('card');
            if (this.customAch)
              this.selectedPaymentOptions = 'card';
          }
        });
    },
    validateMember() {
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + "/member/" + this.selectedMember + "/validate");
    },
    processProratedAmount() {
      if (this.product && this.startInAcademyDate && this.academyBillDay && this.academyBillDay > 0) {
        const today = new Date();
        const academyBillDate = new Date();
        academyBillDate.setDate(this.academyBillDay);
        academyBillDate.setHours(1);
        const daysBetween = this.daysBetween(academyBillDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          academyBillDate.setMonth(academyBillDate.getMonth() + 1);
        }
        this.proratedDays = this.daysBetween(academyBillDate, today);
        this.futureBillDateString = this.$d(academyBillDate, 'month_day_year', this.$i18n.locale);

        const monthlyPrice = this.product.amountDecimal;
        const daysLeft = this.proratedDays;
        let totalDays = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
        let pricePerDay = Math.round((monthlyPrice / totalDays) * 100) / 100;
        this.proratedAmount = Math.round(daysLeft * pricePerDay * 100) / 100;
        this.proratedAmountPerDay = pricePerDay;
      } else if (this.product && this.trialPeriodDate) {
        const today = new Date();
        today.setHours(0);
        const futureDate = new Date(this.trialPeriodDate);
        const daysBetween = this.daysBetween(futureDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          futureDate.setMonth(futureDate.getMonth() + 1);
        }
        this.proratedDays = this.daysBetween(futureDate, today);
        this.futureBillDateString = this.$d(futureDate, 'month_day_year', this.$i18n.locale);

        const monthlyPrice = this.product.amountDecimal;
        const daysLeft = this.proratedDays;
        let totalDays = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
        let pricePerDay = Math.round((monthlyPrice / totalDays) * 100) / 100;
        this.proratedAmount = Math.round(daysLeft * pricePerDay * 100) / 100;
        this.proratedAmountPerDay = pricePerDay;
      }
    },
    isMonthlyMembership() {
      if (this.product && this.product.interval == 'month')
        return true;
      else
        return false;
    },
    savedCardPurchase() {
      this.waitingForPayment = true;
      this.handlingCharges = true;


      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }

      this.processTrialPeriod();
      this.back = false;
      this.handlingCharges = true;

      const items = this.setupItems();
      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      if (this.trialPeriodDate)
        this.trialPeriodDate.setHours(4);
      const postData = {
        items: items,
        saveCreditCard: null,
        processedBy: "desk",
        buyer: this.selectedMember,
        productId: this.productId,
        quantity: this.quantity,
        email: this.email,
        transactionTime: new Date(),
        sendNotification: false,
        discountPercent: discountPercent,
        manualRecurrence: this.product.manualRecurrence,
        discountAmount: discountAmount,
        discountedTotal: this.discountedTotal,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        trialPeriodEndDate: this.trialPeriodDate,
        trialPeriodDays:
          this.trialPeriodDate && this.trialPeriodDays > 0
            ? this.trialPeriodDays
            : null,
      };
      let postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/onetime/savedcard";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.navigateTo({
              name: "transactions",
              query: { status: "success" },
            });
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          this.waitingForPayment = false;
          if (e.response.status == 500) {
            const errData = e.response.data;
            this.showAlert(
              "error",
              this.$t("message.error_code") + ": " + errData.type,
              errData.message,
              15000
            );
          } else {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    async getPaymentMethod() {
      try {
        const response = await Api()
          .get(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/customer/" +
            this.selectedMember
          );
        if (response) {
          if (response.data.invalid) {
            if (this.paymentsReady) {
              console.log('User does not have a valid stripe customer. Will get createed at moment of sale')
            }
          } else {
            this.buyer = { email: response.data.email, name: response.data.name };
            if (response.data.paymentMethod) {
              this.paymentMethod = response.data.paymentMethod;
              /*
              if (this.product && this.product.type == "service")
                this.saveCreditCard = true;
              */
              if (this.paymentMethod.expYear && this.paymentMethod.expMonth) {
                let expDate = new Date();
                expDate.setFullYear(this.paymentMethod.expYear);
                expDate.setMonth(this.paymentMethod.expMonth - 1);
                this.paymentMethod.expDate = expDate;
                const today = new Date();
                if (expDate > today) this.paymentMethod.expired = false;
                else this.paymentMethod.expired = true;
              }
            } else {
              this.paymentMethod = null;
            }
          }
        }
      } catch (err) {
        this.showAlert("error", this.$t("message.error_generic111"));
      }
    },
    checkAmountValidity(q) {
      if (!isNaN(q) && q != "") {
        const quantityNumber = new Number(q).valueOf();
        if (
          !this.product.unlimited &&
          this.product.availableQuantity != null &&
          this.product.availableQuantity >= 0
        ) {
          //console.log('Is ['+this.product.availableQuantity+'] bigger than ['+quantityNumber+']? ', this.product.availableQuantity >= quantityNumber)
          if (this.product.availableQuantity >= quantityNumber)
            this.validQuantity = true;
          else this.validQuantity = false;
        } else {
          this.validQuantity = true;
        }
      } else {
        this.validQuantity = false;
      }
      if (this.validQuantity && this.quantity <= 0) this.validQuantity = false;
    },
    payCash() {
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      if (!this.product) {
        this.showAlert("error", this.$t("message.fill_all_params"));
        return;
      }
      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionStartDate = this.subscriptionStartDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      this.back = false;
      this.handlingCharges = true;
      const items = this.setupItems();
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }
      const postData = {
        items: items,
        processedBy: "desk",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        buyer: this.selectedMember,
        productId: this.productId,
        quantity: this.quantity,
        email: this.email,
        manualRecurrence: this.product.manualRecurrence,
        documentTemplate: this.selectedContract,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        transactionDate: subscriptionStartDate ? subscriptionStartDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        paidWith: this.paidWith
      };
      const postUrl =
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/payments/onetime/cash";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.navigateTo({ name: 'viewtransaction', params: { sessionId: data.transaction._id } })
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    loadProductsPackage() {
      Api()
        .get("admins/managedacademy/" + this.$store.state.managedAcademy + "/products/packages/" + this.packageId)
        .then((response) => {
          const productsPackage = response.data.data;
          this.product = productsPackage.mainProduct;
          this.productId = productsPackage.mainProduct._id;
          this.loading = false;
          this.loadProductExtras();
          //Handle extra products
          //addExtraProductSwitch
          if (productsPackage.products) {
            this.addExtraProductSwitch = true;
            this.extraOptionsPanel = 0;
            this.extraProducts = [];
            for (const p of productsPackage.products) {
              this.extraProducts.push(p);
            }
            this.calculateTotalExtra();
          }
        })
        .catch((e) => {
          if (e) {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    loadProduct() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/" +
          this.productId
        )
        .then((response) => {
          this.product = response.data;
          this.loading = false;
          this.loadProductExtras();
        })
        .catch((e) => {
          this.loading = false;
          console.log('1529: ', e)
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    loadProductExtras() {
      if (!this.isMonthlyMembership())
        this.startInAcademyDate = false;
      if (this.product && this.product.type != "good" && this.paymentTypes.length == 0) {
        this.getPaymentTypes();
        this.validQuantity = true;
      }
      if (this.product && this.product.isMembership) {
        this.quantity = 1;
        this.hideQuantity = true;
        if (this.stripeReady && this.paymentsModule != 'infinicept' && !this.isSmallerDisplay()) {
          this.getTerminalAvailability();
        }
        this.getExtraProducts();
      }
      if (this.product && this.product.type == "good" && this.product.altStripeAccount)
        this.altStripeAccount = this.product.altStripeAccount;
      if (this.product.isMembership) {
        Api()
          .get("documents/academy/" + this.$store.state.managedAcademy + "/contracts")
          .then((response) => {
            const data = response.data;
            if (data) {
              for (const c of data.contracts)
                this.contracts.push({ value: c._id, text: c.name });
              if (this.product.documentTemplate) {
                this.assignContract = true;
                this.selectedContract = this.product.documentTemplate;
              }
            }
          });
        this.isSubscription = true;
      }
      if (this.product && this.product.type == "good" &&
        (this.product.membershipDays > 0
          || this.product.membershipMonths > 0)) {
        //yyyy-mm-dd
        const date = new Date();
        date.setHours(23);
        date.setMinutes(59);
        if (this.product.membershipDays > 0) {
          date.setDate(date.getDate() + this.product.membershipDays);
        } else if (this.product.membershipMonths > 0) {
          date.setMonth(date.getMonth() + this.product.membershipMonths);
        }
        this.subscriptionEndDate = date;
        this.subscriptionEndDateCheckbox = true;
      } else if (this.product && this.product.type == "good" &&
        this.product.membershipCheckins > 0) {
        this.subscriptionCheckins = this.product.membershipCheckins;
        this.subscriptionEndCheckinsCheckbox = true;
      }
    },
    processTrialPeriod() {
      if (this.trialPeriodDate) {
        const now = new Date();
        const future = this.trialPeriodDate;
        this.trialPeriodDays = Math.abs(this.daysBetween(now, future));
      }
    },
    stringToDate(s) {
      if (s) {
        const future = new Date();
        var split = s.split("-");
        future.setFullYear(split[0]);
        future.setMonth(parseInt(split[1]) - 1);
        future.setDate(split[2]);
        return future;
      }
    },
    async processCustomAchRecurrentPayment(postData, sendNotification) {
      try {
        this.handlingCharges = true;
        /*eslint-disable no-undef*/
        this.stripeInstance = Stripe(Environment.get("VUE_APP_STRIPE_PUBLISHABLE_KEY"), {
          stripeAccount: this.mainStripeAccountId
        });
        if (this.selectedPaymentOptions.indexOf('acss_debit') >= 0) {
          const res = await Api2()
            .post("payments/managedacademy/" +
              this.$store.state.managedAcademy +
              "/ach/init/subscription", postData);
          const resData = res.data;
          this.clientSecret = resData.clientSecret;
          this.stripeCustomerId = resData.customer;
          this.paymentIntentId = resData.paymentIntentId;
          this.customAchPostData.transactionId = resData.transactionId;

          if (sendNotification) {
            if (resData.transactionId)
              this.navigateTo({ name: 'viewtransaction', params: { sessionId: resData.transactionId } });
            else
              console.error(this.$t('message.sell_product_plus_acss_debit_start_failed'));
            return;
          }

          const { paymentIntent, error } = await this.stripeInstance.confirmAcssDebitPayment(
            this.clientSecret,
            {
              payment_method: {
                billing_details: {
                  name: this.buyer.name,
                  email: this.buyer.email,
                },
              },
            }
          );
          if (error) {
            console.error(this.$t('message.sell_product_plus_acss_debit_error'), error.message);
            this.handlingCharges = false;
            this.showAlert("error", error.message, null, 7000);
          } else if (paymentIntent && paymentIntent.status != 'canceled') {
            this.handlingCharges = true;
            // Send payment_method to backend to attach to subscription
            this.customAchPostData.paymentIntentId = paymentIntent.id;
            this.customAchPostData.status = paymentIntent.status;
            this.customAchPostData.stripeCustomerId = this.stripeCustomerId;
            this.customAchPostData.paymentMethodId = paymentIntent.payment_method;
            this.customAchPostData.skipPaymentConfirmation = true;
            this.completeCustomAchPurchase();
          }
        } else if (this.selectedPaymentOptions.indexOf('nz_bank_account') >= 0) {
          //test: 11-0000-00000000-10
          /*
          this.buyer.account_number = '0000000';
          this.buyer.bank_code = '11';
          this.buyer.branch_code = '0000';
          this.buyer.suffix = '10';
          */
          if (sendNotification) {
            postData.sendNotification = true;
            const res = await Api2()
              .post("payments/managedacademy/" +
                this.$store.state.managedAcademy +
                "/ach/init/subscription", postData);
            const resData = res.data;
            if (resData.transactionId) {
              this.navigateTo({ name: 'viewtransaction', params: { sessionId: resData.transactionId } });
            } else {
              console.error(this.$t('message.sell_product_plus_acss_debit_start_failed'));
              this.showAlert("error", this.$t('message.sell_product_plus_acss_debit_start_failed'), null, 7000);
            }
            return;
          }
          if (this.selectedPaymentOptions.indexOf('nz_bank_account') >= 0) {
            if (!this.buyer.account_number ||
              !this.buyer.bank_code ||
              !this.buyer.branch_code ||
              !this.buyer.suffix) {
              this.showAlert("error", this.$t("message.sell_product_plus_complete_bank_information"));
              this.handlingCharges = false;
              return;
            }
          }

          const { paymentMethod, error } = await this.stripeInstance.createPaymentMethod({
            type: 'nz_bank_account',
            nz_bank_account: {
              account_number: this.buyer.account_number, // 7-digit account base
              bank_code: this.buyer.bank_code, // 2 digits
              branch_code: this.buyer.branch_code,     // 4-digit branch code
              suffix: this.buyer.suffix
            },
            billing_details: {
              name: this.buyer.name,
              email: this.buyer.email,
            },
          });
          if (error) {
            console.error(this.$t('message.sell_product_plus_acss_debit_error'), error.message);
            this.handlingCharges = false;
            this.showAlert("error", error.message, null, 7000);
          } else if (paymentMethod) {
            this.handlingCharges = true;
            postData.paymentMethodId = paymentMethod.id;
            const res = await Api2()
              .post("payments/managedacademy/" +
                this.$store.state.managedAcademy +
                "/ach/init/subscription", postData);
            const resData = res.data;
            this.clientSecret = resData.clientSecret;
            this.stripeCustomerId = resData.customer;
            this.paymentIntentId = resData.paymentIntentId;
            this.customAchPostData.paymentIntentId = this.paymentIntentId;
            this.customAchPostData.stripeCustomerId = this.stripeCustomerId;
            if (!this.paymentIntentId) {
              this.showAlert("error", this.$t("message.sell_product_plus_no_payment_intent"));
              this.handlingCharges = false;
              return;
            }
            this.customAchPostData.transactionId = resData.transactionId;
            if (postData.trialPeriodEndDate)
              this.customAchPostData.paymentMethodConfirmed = true;
            this.completeCustomAchPurchase();
          }
        } else {
          this.showAlert("error", this.$t("message.sell_product_plus_invalid_ach_payment_type"), this.selectedPaymentOptions);
          this.handlingCharges = false
          return;
        }
      } catch (err) {
        console.log('1735: ', err)
        this.showAlert("error", this.$t("message.error_generic"));
        this.handlingCharges = false
      }
    },
    completeCustomAchPurchase() {
      //Send it to the back end to record our transaction
      Api()
        .post(
          "payments/managedacademy/" +
          this.$store.state.managedAcademy +
          "/ach/complete/subscription", this.customAchPostData
        )
        .then((response) => {
          if (response.data) {
            this.handlingCharges = false;
            this.showAlert(
              "success",
              this.$t("message.payment_completed")
            );
            this.navigateTo({ name: 'viewtransaction', params: { sessionId: response.data.transaction._id } });
          }
        }).catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.handlingCharges = false;
        });
    },
    initCheckout(sendNotification) {
      this.errorMessage = null;
      if (!this.product) {
        this.showAlert("error", this.$t("message.fill_all_params"));
        return;
      }
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      //this.paymentForm = true;
      this.handlingCharges = true;
      this.processTrialPeriod();
      this.back = false;
      const items = this.setupItems();
      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.handlingCharges = false;
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.trial_end_too_close"));
          this.handlingCharges = false;
          return;
        }
      }
      let prorated = null;
      if (this.product.type == "service" && this.startInAcademyDate && this.proratedAmount > 0) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
        const today = new Date();
        const academyBillDate = new Date();
        academyBillDate.setDate(this.academyBillDay);
        academyBillDate.setHours(1);
        const daysBetween = this.daysBetween(academyBillDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          academyBillDate.setMonth(academyBillDate.getMonth() + 1);
        }
        this.trialPeriodDate = academyBillDate;
      } else if (this.product.type == "service" && this.startInFuture && this.addProratedAmount) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
      }
      //Add one for NZ
      if (this.trialPeriodDate) {
        this.trialPeriodDate.setHours(4);//Set the start to the morning
        if (!this.isAtLeastThreeDays(this.trialPeriodDate)) {
          this.showAlert("error", this.$t("message.trial_end_too_close"));
          this.handlingCharges = false;
          return;
        }
      }
      const discountExpirationDate = this.getDiscountExpirationDate();
      let postData = {
        items: items,
        saveCreditCard: this.saveCreditCard ? this.saveCreditCard : null,
        processedBy: sendNotification ? "user" : "desk",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        discountDuration: this.discountDurationInMonths ? new Number(this.discountDurationInMonths).valueOf() : null,
        discountExpirationDate: discountExpirationDate,
        buyer: this.selectedMember,
        prorated: prorated,
        productId: this.productId,
        quantity: this.quantity,
        email: this.email,
        transactionTime: new Date(),
        paymentTypes: this.customAch ? [this.selectedPaymentOptions] : this.selectedPaymentOptions,
        manualRecurrence: this.product.manualRecurrence,
        sendNotification: sendNotification,
        documentTemplate: this.selectedContract,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        trialPeriodEndDate: this.startInFuture || this.startInAcademyDate ? this.trialPeriodDate : null,
        /*
        trialPeriodDays:
          this.trialPeriodDate && this.trialPeriodDays > 0
            ? this.trialPeriodDays
            : null,
        */
      };
      if (this.customAch && this.isCustomAchPaymentOption()) {
        this.customAchPostData = postData;
        this.customAchPostData.paymentType = this.selectedPaymentOptions;//This is not an array
        return this.processCustomAchRecurrentPayment(postData, sendNotification);
      }
      if (this.paymentsModule == 'infinicept') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.getTransactionTotal();
        postData.paymentsModule = 'infinicept';
        this.navigateTo({
          name: "inifnicept-checkout",
          params: { transactionData: postData }
        });
        return;
      } else if (this.paymentsModule == 'easypay') {
        postData.interval = this.product && this.product.interval ? this.product.interval : null;
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.getTransactionTotal();
        postData.paymentsModule = 'easypay';
        postData.routeFrom = 'regularsell';
        postData.productData = this.product;
        postData.sendNotification = sendNotification;
        return this.navigateTo({
          name: "easypay-checkout",
          params: { transactionData: postData }
        });
      }
      let postUrl =
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/payments/onetime/checkout";
      if (this.product.type == "service") {
        postUrl =
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/recurrent/checkout";
      }
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            if (!sendNotification) {
              if (this.altStripeAccount)
                this.navigateTo({
                  name: "striperedirect_session",
                  params: { sessionId: data.id },
                  query: { altStripeAccount: this.altStripeAccount }
                });
              else
                this.navigateTo({
                  name: "striperedirect_session",
                  params: { sessionId: data.id },
                });
            } else {
              this.handlingCharges = false;
              this.checkoutInitialized = true;
              this.back = true;
              /*
              this.showAlert(
                "success",
                this.$t("message.success"),
                this.$t("message.charge_notification"),
                8000
              );
              */
              //If send notification, go to transaction!
              if (data.transactionId)
                this.navigateTo({ name: 'viewtransaction', params: { sessionId: data.transactionId } });
              else
                this.goBack();
            }
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          if (e.response.data.localizedMessage) {
            this.errorMessage = this.$t("message." + e.response.data.localizedMessage);
            this.showAlert("error", this.errorMessage);
          } else if (e.response.data.message) {
            this.showAlert("error", e.response.data.message);
          } else {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    isCustomAchPaymentOption() {
      const isArray = Array.isArray(this.selectedPaymentOptions);
      if (!isArray && (this.selectedPaymentOptions.indexOf('acss_debit') >= 0 ||
        this.selectedPaymentOptions.indexOf('nz_bank_account') >= 0 ||
        this.selectedPaymentOptions.indexOf('ach') >= 0)) {
        return true;
      }
      return false;
    },
    setupItems() {
      try {
        let items = [];
        for (const i of this.extraProducts) {
          let image = this.userImg(this.academyImage);
          if (i.image) image = this.userImg(i.image);
          items.push({
            name: i.name,
            description:
              !i.description || i.description == "" ? " " : i.description,
            images: [image],
            amount: i.amount,
            amountDecimal: i.free ? 0 : i.amountDecimal,
            currency: this.currency,
            quantity: 1,
            product: i._id,
          });
        }
        return items;
      } catch (err) {
        console.log('1953: ', err);
      }
      return [];
    },
    cancel() {
      if (this.isDialog) {
        this.$emit("close");
        return;
      }
      if (this.stripeSessionId) {
        this.handlingCharges = true;
        this.navigateTo({ name: "products" });
      } else {
        this.navigateTo({ name: "products" });
      }
    },
  },
};

</script>

<style scoped>
/* Modal-like card per design: centered, constrained width, close (X) button */
.sell-membership-card {
  position: relative;
  width: 100%;
  max-width: 600px;
  background-color: var(--v-plusSurface-base);
  border: 1px solid var(--v-plusBorder-base);
  border-radius: 12px;
  padding: 1.5rem;
}

.sell-membership-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

/* When rendered inside a v-dialog the dialog provides the modal frame */
.sell-membership-card--dialog {
  max-width: none;
  border: none;
}

/* Warning banner — light mode per design (#FFF8E1 bg, #F57C00 icon).
   Dark mode styles are applied inline via isDarkMode(), following the
   PaymentConnectionAlert pattern. */
.sell-warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: var(--v-plusAmberLighten5-base);
}

/* Outlined date field with floating label, matching Vuetify outlined inputs */
.sell-date-field {
  position: relative;
}

.sell-date-field-label {
  position: absolute;
  top: -9px;
  left: 8px;
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.2;
  background-color: var(--v-plusSurface-base);
  z-index: 1;
}

/* Toggles — light mode palette per design:
   off: #E8EAF6 track / #FAFAFA thumb; on: #1A237E track / #FFFFFF thumb.
   Dark mode keeps the existing global switch styles from App.vue. */
.sell-product-fields ::v-deep .v-input--switch.theme--light .v-input--switch__track {
  opacity: 1 !important;
  background-color: #E8EAF6 !important;
}

.sell-product-fields ::v-deep .v-input--switch.theme--light .v-input--switch__thumb {
  background-color: #FAFAFA !important;
}

.sell-product-fields ::v-deep .v-input--switch.theme--light.v-input--is-dirty .v-input--switch__track {
  background-color: #1A237E !important;
}

.sell-product-fields ::v-deep .v-input--switch.theme--light.v-input--is-dirty .v-input--switch__thumb {
  background-color: #FFFFFF !important;
}
</style>
